"use client"

import type React from "react"
import { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useConnectorFlow } from "../../../context/ConnectorFlowContext"
import { useSyncDirection } from "../../../hooks/useSyncDirection"
import { ROUTES } from "../../../constants/sync-flow"
import type { SyncConfiguration } from "../../../types/sync-flow"

// Components
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner"
import StepIndicator from "../shared/StepIndicator"
import PlatformIcon from "../shared/PlatformIcon"

import "./SyncDirection.css"

const SyncDirectionConnectScreen: React.FC = () => {
  const { dispatch } = useConnectorFlow()
  const navigate = useNavigate()
  const { sections, loading, errors, hasErrors, toggleSection, toggleRule, saveConfiguration } = useSyncDirection()

  const handleBack = useCallback(() => {
    navigate(ROUTES.MAP_FIELDS + `?id=1`)
  }, [navigate])

  const handleContinue = useCallback(async () => {
    const success = await saveConfiguration()
    if (success) {
      // Create proper SyncConfiguration object for dispatch
      const syncConfig: SyncConfiguration = {
        sourceSystem: "Salesforce",
        targetSystem: "HubSpot",
        syncType: "bidirectional",
        description: "Two-way sync of Salesforce lead and contacts to HubSpot contacts.",
        rules: {
          creating: sections.find((s) => s.id === "creating")?.rules || [],
          deleting: sections.find((s) => s.id === "deleting")?.rules || [],
        },
      }

      dispatch({
        type: "SET_SYNC_CONFIGURATION",
        payload: syncConfig, // Now passing the correct type
      })

      setTimeout(() => {
        navigate(ROUTES.REVIEW_SETUP)
      }, 1000)
    }
  }, [saveConfiguration, dispatch, sections, navigate])

  const getPlatformIcon = useCallback((platform: string) => {
    return <PlatformIcon platform={platform as "hubspot" | "salesforce"} />
  }, [])

  const handleLearnMore = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    // Add your learn more logic here
    // You could open a modal, navigate to a help page, etc.
  }, [])

  const getObjectName = (name: string) =>{
    const objectName = localStorage.getItem(name) || 'contact';
    return objectName|| 'Contact'
    // return objectName.replace(objectName.charAt(0),objectName.charAt(0).toUpperCase())|| 'Contact'
  }

  return (
    <div className="sync-direction">
      <div className="sync-direction-header">
        <h1>Configure Sync Rules</h1>
        <p>Define how your data should sync between Salesforce and HubSpot.</p>
        <StepIndicator currentStep={5} stepLabel="Sync Direction" completedSteps={[1, 2, 3, 4]} />
      </div>

      <div className="sync-overview">
        <div className="sync-overview-card">

          <div className="platform-section">
            <PlatformIcon platform="hubspot" />
            <h3>HubSpot { getObjectName('source_object') }</h3>
          </div>
          

          <div className="sync-arrows">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
              <path d="M17 11l4 4-4 4" />
              <path d="M7 13l-4-4 4-4" />
              <path d="M21 15H3" />
              <path d="M21 9H3" />
            </svg>
          </div>

          <div className="platform-section">
            <PlatformIcon platform="salesforce" />
            <h3>Salesforce { getObjectName('target_object') }</h3>
          </div>
          

          <div className="checkmark">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
            </svg>
          </div>
        </div>

        <p className="sync-description">Two-way sync of Salesforce lead and contacts to HubSpot contacts.</p>
      </div>

      {errors.rules && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {errors.rules}
        </div>
      )}

      <div className="sync-sections">
        {sections.map((section) => (
          <div key={section.id} className="sync-section">
            <div className="section-header" onClick={() => toggleSection(section.id)}>
              <div className="section-title">
                <span className={`expand-icon ${section.expanded ? "expanded" : ""}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </span>
                <h3>{section.title}</h3>
              </div>
              {errors[section.id] && <span className="section-error">⚠️</span>}
            </div>

            {section.expanded && (
              <div className="section-content">
                <div className="rules-grid">
                  <div className="grid-headers">
                    <div className="header-left">Source</div>
                    <div className="header-right">Target</div>
                  </div>

                  {section.rules.map((rule) => (
                    <div key={rule.id} className={`rule-row ${rule.enabled ? "enabled" : "disabled"}`}>
                      <div className="rule-source">
                        {getPlatformIcon(rule.sourceIcon)}
                        <span className="rule-text">{rule.sourceAction}</span>
                      </div>

                      <div className="rule-arrow">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14" />
                          <path d="M12 5l7 7-7 7" />
                        </svg>
                      </div>

                      <div className="rule-target">
                        {getPlatformIcon(rule.targetIcon)}
                        <span className="rule-text">{rule.targetAction}</span>
                      </div>

                      <div className="rule-toggle">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            onChange={() => toggleRule(section.id, rule.id)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                {errors[section.id] && (
                  <div className="section-error-message">
                    <span className="error-icon">⚠️</span>
                    {errors[section.id]}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="sync-note">
        <div className="note-content">
          <span className="note-label">Note:</span>
          <span className="note-text">
            If a record with the same email address exists in Salesforce, the integration will sync to the existing
            record first regardless of the above setting.
          </span>
          <button
            type="button"
            className="learn-more-link"
            onClick={handleLearnMore}
            aria-label="Learn more about sync rules"
          >
            Learn more
          </button>
        </div>
      </div>

      <div className="actions-section">
        <button className="back-button" onClick={handleBack}>
          Back
        </button>
        <button className="continue-button" onClick={handleContinue} disabled={loading || hasErrors}>
          {loading ? (
            <>
              <LoadingSpinner size="small" />
              Saving...
            </>
          ) : (
            "Continue"
          )}
        </button>
      </div>
    </div>
  )
}

export default SyncDirectionConnectScreen
